import React, { useContext, useMemo, useState } from "react";
import { Send, Trash2 } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import EntityFormModal from "modules/swiftboard/components/EntityFormModal";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import { useAlert } from "context/AlertContext";
import { AuthContext } from "context/AuthContext";
import useConfirm from "../../components/useConfirm";
import useServerList from "../../components/useServerList";
import { MANAGE_ROLE } from "../../features/builder.constants";
import { errorMessage } from "../../features/builder.utils";
import apiReminderChannel from "./api";
import { channelFields } from "./features/channel.fields";
import { channelValidation } from "./features/channel.validation";
import { loadChannelDependencies } from "./features/channel.dependencies";
import { channelFilterFields } from "./features/channel.filter";
import { toChannelPayload } from "./features/channel.utils";
import { getChannelColumns } from "./channel-list/channelColumns";
import ChannelRowActions from "./channel-list/ChannelRowActions";
import ChannelViewDrawer from "./channel-dialogs/ChannelViewDrawer";

const createChannel = (values) => apiReminderChannel.create(toChannelPayload(values, false));
const updateChannel = (id, values) => apiReminderChannel.update(id, toChannelPayload(values, true));

// Reminder Setup -> Channel (tbl_rem_channel): how a reminder can be delivered (Email, In-app, later SMS / WhatsApp).
const Channel = () => {
  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);
  const canManage = user?.user_role?.role === MANAGE_ROLE;
  const list = useServerList(apiReminderChannel.getAll, { loadError: "Failed to load channels." });
  const { confirmData, openConfirm, handleConfirmClose } = useConfirm();

  const [form, setForm] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [busy, setBusy] = useState(null);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleTestSend = async (row) => {
    setBusy({ id: row.id, action: "test" });
    try {
      const res = await apiReminderChannel.testSend(row.id);
      showAlert({ type: "success", message: res.data?.message || "Test message sent." });
    } catch (err) {
      showAlert({ type: "error", title: "Test not sent", message: errorMessage(err, "The test message could not be sent.") });
    } finally {
      setBusy(null);
    }
  };

  const deleteChannels = async (ids) => {
    const confirmed = await openConfirm({
      title: ids.length > 1 ? "Delete Selected" : "Delete Channel",
      message: `Delete ${ids.length > 1 ? `${ids.length} channels` : "this channel"}? Channels used by reminder templates or rule steps can't be deleted.`,
      confirmText: "Delete",
      variant: "danger",
      icon: <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      const res = await apiReminderChannel.delete(ids);
      showAlert({ type: "success", message: res.data?.message || "Deleted successfully." });
      list.resetSelection();
      list.reload();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err, "Delete failed.") });
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns = useMemo(() => getChannelColumns(list.start), [list.start]);

  const renderRowActions = (row) => (
    <ChannelRowActions
      row={row}
      canManage={canManage}
      busy={busy}
      onView={setViewRecord}
      onTestSend={handleTestSend}
      onEdit={(target) => setForm({ id: target.id })}
      onDelete={(target) => deleteChannels([target.id])}
    />
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4 pb-4">
        <TableToolBar
          onSearch={list.handleSearch}
          onRefresh={list.handleRefresh}
          onCreate={() => setForm({ id: null })}
          onDeleteSelected={canManage ? () => deleteChannels(list.selectedRows) : undefined}
          selectedCount={list.selectedRows.length}
          resetSearchTrigger={list.resetTrigger}
          canCreate={canManage}
          resultCount={list.totalCount}
          advanceFilter={<GlobalAdvanceFilter fields={channelFilterFields} onApply={list.handleApplyFilters} resetKey={list.resetTrigger} />}
        />

        {!list.loading && list.records.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <NoDataState
              icon={Send}
              title={list.hasQuery ? "No channels match" : "No reminder channels yet"}
              message={
                list.hasQuery
                  ? "No channels match your search or filters. Try adjusting them."
                  : "Add the channels reminders may be sent through. Email and In-app notification can send today."
              }
              showBack={false}
              showRefresh={list.hasQuery}
              onRefresh={list.handleRefresh}
              showCreate={canManage && !list.hasQuery}
              createText="Add Channel"
              onCreate={() => setForm({ id: null })}
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border">
              <TableGrid
                data={list.records}
                loading={list.loading}
                columns={columns}
                pageSize={list.query.size}
                sortConfig={list.query.sort}
                onSort={list.handleSort}
                onSelectionChange={list.setSelectedRows}
                resetSelectionTrigger={list.selectionReset}
                selectable={canManage}
                showActions
                renderActions={renderRowActions}
              />
            </div>
            <Pagination totalCount={list.totalCount} pageSize={list.query.size} currentPage={list.query.page} onPageChange={list.handlePageChange} selectedCount={list.selectedRows.length} />
          </>
        )}
      </div>

      <EntityFormModal
        isOpen={Boolean(form)}
        onClose={() => setForm(null)}
        id={form?.id}
        title="Channel"
        fetchById={apiReminderChannel.getById}
        createFn={createChannel}
        updateFn={updateChannel}
        dependenciesLoader={loadChannelDependencies}
        fields={channelFields}
        validationSchema={channelValidation}
        onSuccess={list.reload}
      />

      {viewRecord && <ChannelViewDrawer record={viewRecord} onClose={() => setViewRecord(null)} />}
      {confirmData.isOpen && <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />}
    </div>
  );
};

export default Channel;
